import { create } from 'zustand'
import {
  ContactRole,
  CreateContactRoleRequest,
  UpdateContactRoleRequest
} from '../types/contact'
import { contactRoleService } from '../services/contactRoleService'

interface ContactRoleState {
  // State
  roles: ContactRole[]
  loading: boolean
  error: string | null
  selectedRole: ContactRole | null

  // Actions
  loadRoles: (activeOnly?: boolean) => Promise<void>
  loadRole: (id: string) => Promise<void>
  createRole: (data: CreateContactRoleRequest) => Promise<ContactRole>
  updateRole: (id: string, data: UpdateContactRoleRequest) => Promise<ContactRole>
  deleteRole: (id: string) => Promise<void>
  setSelectedRole: (role: ContactRole | null) => void
  reset: () => void
}

const useContactRoleStore = create<ContactRoleState>((set) => ({
  // Initial State
  roles: [],
  loading: false,
  error: null,
  selectedRole: null,

  // Actions
  loadRoles: async (activeOnly?: boolean) => {
    set({ loading: true, error: null })
    try {
      const roles = await contactRoleService.getContactRoles(activeOnly)
      set({ roles, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load contact roles',
        loading: false
      })
    }
  },

  loadRole: async (id: string) => {
    set({ loading: true, error: null })
    try {
      const role = await contactRoleService.getContactRole(id)
      set({ selectedRole: role, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load contact role',
        loading: false
      })
    }
  },

  createRole: async (data: CreateContactRoleRequest) => {
    const role = await contactRoleService.createContactRole(data)
    // Reload roles to get fresh data
    return role
  },

  updateRole: async (id: string, data: UpdateContactRoleRequest) => {
    const updated = await contactRoleService.updateContactRole(id, data)
    // Reload roles to get fresh data
    return updated
  },

  deleteRole: async (id: string) => {
    await contactRoleService.deleteContactRole(id)

    // Remove from local state (or mark as inactive)
    set((state) => ({
      roles: state.roles.filter((role) => role.id !== id),
      selectedRole: state.selectedRole?.id === id ? null : state.selectedRole
    }))
  },

  setSelectedRole: (role: ContactRole | null) => {
    set({ selectedRole: role })
  },

  reset: () =>
    set({
      roles: [],
      loading: false,
      error: null,
      selectedRole: null
    })
}))

export default useContactRoleStore
